import { BoltOutlined, RefreshOutlined, TagOutlined, UndoOutlined } from '@mui/icons-material'
import { FC, ReactNode, useRef } from 'react'
import { PostTextAction, PostTextStatus } from 'models/socialCampaign'

import CircularProgress from '@mui/material/CircularProgress'
import { GRAY_700 } from 'constants/styling/theme'
import { MUIButton } from 'components/common/MUIButton'
import { SystemMessage } from 'components/common/SystemMessage'
import Typography from '@mui/material/Typography'
import styles from './PostTextActions.module.sass'
import { useTranslation } from 'react-i18next'

interface Props {
  status: PostTextStatus
  /** The last action asked for on the text, the one a retry runs again */
  lastAction: PostTextAction | null
  /** The action the editor saw answered on this post: what it did is said once, until the next one or the client types */
  done: PostTextAction | null
  /** Undo put back the text the action replaced */
  restored: boolean
  /** Another action of the editor is on its way */
  disabled: boolean
  /** The actions the backend would take on the text as it stands (`textActionsOf`) */
  available: Record<PostTextAction, boolean>
  /** The last action failed and the text is still the one it was asked on */
  showFailure: boolean
  onRun: (action: PostTextAction) => void
  /** Offered while the text is still the one the action wrote and the editor knows the text it replaced */
  onUndo?: () => void
}

/** Prototype v3 `formHtml`: Regenerate (refresh), Shorter (bolt), More hashtags (the "#" glyph), Material outlined. */
const ACTIONS: { action: PostTextAction, icon: ReactNode }[] = [
  { action: PostTextAction.REGENERATE, icon: <RefreshOutlined /> },
  { action: PostTextAction.SHORTER, icon: <BoltOutlined /> },
  { action: PostTextAction.MORE_HASHTAGS, icon: <TagOutlined /> },
]

/**
 * The AI actions on the post's one text (ISSUE-3619, prototype v3 `.mc3-ai`, v2 d11): a row of small buttons under the
 * text, a strip that scrolls sideways on a phone (the swipe between posts leaves it alone). While an action rewrites
 * the text every action waits and a line says which one runs; a failed action says the text is kept and offers to try
 * it again. The line is a live region present from the start, so the action running and its result are announced.
 * Once an action replaced the text, Undo beside the line puts the text before it back, and the line says so.
 * A button that waits keeps the focus (`aria-disabled`), as the editor's other actions do.
 */
export const PostTextActions: FC<Props> = ({ status, lastAction, done, restored, disabled, available, showFailure, onRun, onUndo }) => {
  const { t } = useTranslation(['social_campaign'])
  const pending = status === PostTextStatus.PENDING
  const buttons = useRef<Partial<Record<PostTextAction, HTMLButtonElement | null>>>({})

  let line: ReactNode = null
  if (pending && lastAction) line = <><CircularProgress size="1.6rem" color="inherit" aria-hidden="true" /><span>{t(`editor.ai.pending.${lastAction}`)}</span></>
  else if (restored) line = t('editor.ai.restored')
  else if (done) line = t(`editor.ai.done.${done}`)

  return (
    <div className={styles.wrap}>

      <div className={styles.actions} role="group" aria-label={t('editor.ai.label')} data-swipe-ignore="">
        {ACTIONS.map(({ action, icon }) => (
          <MUIButton
            key={action}
            ref={(button) => { buttons.current[action] = button }}
            type="secondaryBorder"
            size="sm"
            className={styles.action}
            startIcon={icon}
            disabled={disabled || pending || !available[action]}
            isFocusableWhenDisabled
            onClick={() => onRun(action)}
          >
            {t(`editor.ai.action.${action}`)}
          </MUIButton>
        ))}
      </div>

      <div className={styles.result}>

        <Typography variant="text-sm" component="p" color={GRAY_700} className={styles.status} role="status" data-text-status="">{line}</Typography>

        {/* beside the live region, not in it: its label is not part of what the line announces */}
        {onUndo && !pending &&
          <MUIButton
            type="secondaryBorder"
            size="sm"
            className={styles.undo}
            startIcon={<UndoOutlined />}
            disabled={disabled}
            isFocusableWhenDisabled
            onClick={onUndo}
          >
            {t('editor.ai.undo')}
          </MUIButton>
        }

      </div>

      {showFailure && lastAction &&
        <div className={styles.failure}>
          <SystemMessage
            variant="warning"
            // said at once, like the editor's other refusals
            role="alert"
            message={t(`editor.ai.failed.${lastAction}`)}
            isRawContent
            actions={
              <MUIButton
                type="secondaryBorder"
                size="sm"
                disabled={disabled}
                isFocusableWhenDisabled
                onClick={() => {
                  // this button goes with the failure: the action it runs again keeps the focus while it waits
                  buttons.current[lastAction]?.focus()
                  onRun(lastAction)
                }}
              >
                {t('editor.ai.retry')}
              </MUIButton>
            }
          />
        </div>
      }

    </div>
  )
}
